import { useEffect, useState, useRef } from 'react'
import { css, ThemedCssFunction } from 'styled-components'

export const horizontalScale = (size: number, isMobile?: boolean) => {
  return `calc(100vw * (${size} / ${isMobile ? 320 : 1920}))`
}

export const mobileMediaQuery = <OwnProps extends object>(cb: (c: ThemedCssFunction<OwnProps>, scaleFunc: (size: number) => string) => ReturnType<typeof css>) => css`
  @media screen and (max-width: 768px) {
    ${cb(css, (size) => horizontalScale(size, true))}
  }
`

const MOBILE_BREAKPOINT = 768

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}


export const useDidUpdate: typeof useEffect = (callback, deps) => {
  const hasMount = useRef(false);

  useEffect(() => {
    if (hasMount.current) {
      callback();
    } else {
      hasMount.current = true;
    }
  }, [callback, deps]);
};

const stylePrev = {
  htmlStyle: {},
  bodyStyle: {},
};

export function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (symbol) => {
    const rnd = (Math.random() * 16) | 0;
    return (symbol === 'x' ? rnd : (rnd & 0x3) | 0x8).toString(16);
  });
}

function saveStylePrev() {
  const html = window?.document?.querySelector?.(':root') as HTMLElement;
  const body = window?.document?.body as HTMLElement;
  Object.assign(stylePrev, {
    htmlStyle: {
      overflowY: html.style.overflowY || 'unset',
      width: html.style.width || 'unset',
      touchAction: 'auto',
    },
    bodyStyle: {
      overflowY: body.style.overflowY || 'unset',
      width: body.style.width || 'unset',
      touchAction: 'auto',
    },
  });
}

function setStylePrev() {
  const saveStyle = {
    htmlStyle: { ...stylePrev.htmlStyle },
    bodyStyle: { ...stylePrev.bodyStyle },
  };
  Object.assign(stylePrev, {
    htmlStyle: {},
    bodyStyle: {},
  });
  return saveStyle;
}

export function getScrollbarWidth(): number {
  const scrollbarWidth =
    window.innerWidth - (window?.visualViewport?.width || 0) || 0;
  return scrollbarWidth;
}

export const scrollLock = () => {
  const html = window?.document?.querySelector?.(':root') as HTMLElement;
  const body = window?.document?.body as HTMLElement;
  const scrollbarWidth = getScrollbarWidth();
  const bodyWidth =
    scrollbarWidth === 0 ? `100%` : `calc(100% - ${scrollbarWidth}px)`;
  if (html && body) {
    saveStylePrev();
    const style = {
      overflowY: 'hidden',
      paddingRight: `${scrollbarWidth}px`,
      touchAction: 'none',
    };
    Object.assign(body.style, style);
    Object.assign(html.style, style);
  }
};

export const scrollUnlock = () => {
  const html = window?.document?.querySelector?.(':root') as HTMLElement;
  const body = window?.document?.body as HTMLElement;
  if (html && body) {
    const { htmlStyle, bodyStyle } = setStylePrev();
    Object.assign(html.style, htmlStyle);
    Object.assign(body.style, {
      overflowY: 'auto',
      paddingRight: 0,
      touchAction: 'auto',
    });
  }
};